import { createContext, useContext, useReducer, useMemo, useCallback, ReactNode } from 'react'
import {
  DreamityItemResDto,
  DreamityItemSaveReqDto,
  DreamityItemModifyReqDto,
} from '../types/checklist.types'
import { dreamityPlannerRepository } from '@/services/dreamityPlannerRepository'
import { readLocalDreamitySnapshot, getSnapshotUserIdFromToken } from '@/services/dreamityLocalSnapshot'
import { scheduleDebouncedPushDreamitySnapshot } from '@/services/dreamitySyncService'

interface ItemState {
  // planId별 아이템 목록 저장
  itemsMap: Record<string, DreamityItemResDto[]>
  selectedItem: DreamityItemResDto | null
  loading: boolean
  error: string | null
}

type ItemAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PLAN_ITEMS'; payload: { planId: string; items: DreamityItemResDto[] } }
  | { type: 'SET_SELECTED_ITEM'; payload: DreamityItemResDto | null }
  | { type: 'UPDATE_ITEM'; payload: { planId: string; item: DreamityItemResDto } }
  | { type: 'DELETE_ITEM'; payload: { planId: string; itemId: string } }

interface ItemContextType extends ItemState {
  items: DreamityItemResDto[] // 호환성을 위해 flat list 제공
  fetchItems: (dreamId: string, planId: string) => Promise<void>
  fetchItemById: (dreamId: string, itemId: string) => Promise<DreamityItemResDto | null>
  createItem: (dreamId: string, item: DreamityItemSaveReqDto) => Promise<void>
  updateItem: (dreamId: string, itemId: string, item: DreamityItemModifyReqDto) => Promise<void>
  deleteItem: (dreamId: string, itemId: string, planId: string) => Promise<void>
  selectItem: (item: DreamityItemResDto | null) => void
}

const ItemContext = createContext<ItemContextType | undefined>(undefined)

const initialState: ItemState = {
  itemsMap: {},
  selectedItem: null,
  loading: false,
  error: null,
}

function itemReducer(state: ItemState, action: ItemAction): ItemState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_PLAN_ITEMS':
      return {
        ...state,
        itemsMap: {
          ...state.itemsMap,
          [action.payload.planId]: action.payload.items,
        },
      }
    case 'SET_SELECTED_ITEM':
      return { ...state, selectedItem: action.payload }
    case 'UPDATE_ITEM': {
      const { planId, item } = action.payload
      const planItems = state.itemsMap[planId] || []
      return {
        ...state,
        itemsMap: {
          ...state.itemsMap,
          [planId]: planItems.map((i) => (i.itemId === item.itemId ? item : i)),
        },
        selectedItem: state.selectedItem?.itemId === item.itemId ? item : state.selectedItem,
      }
    }
    case 'DELETE_ITEM': {
      const { planId, itemId } = action.payload
      const planItems = state.itemsMap[planId] || []
      return {
        ...state,
        itemsMap: {
          ...state.itemsMap,
          [planId]: planItems.filter((i) => i.itemId !== itemId),
        },
        selectedItem: state.selectedItem?.itemId === itemId ? null : state.selectedItem,
      }
    }
    default:
      return state
  }
}

interface ItemProviderProps {
  children: ReactNode
}

export function ItemProvider({ children }: ItemProviderProps) {
  const [state, dispatch] = useReducer(itemReducer, initialState)

  const fetchItems = useCallback(async (dreamId: string, planId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const items = dreamityPlannerRepository.listItems(dreamId, planId)
      dispatch({ type: 'SET_PLAN_ITEMS', payload: { planId, items } })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  const fetchItemById = async (dreamId: string, itemId: string) => {
    try {
      const uid = getSnapshotUserIdFromToken()
      const item = uid == null ? undefined : readLocalDreamitySnapshot(uid)?.domains.items?.find(
        (i) => i.itemId === itemId && i.dreamId === dreamId
      )
      if (item) {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: item })
        if (item.planId) {
          dispatch({ type: 'UPDATE_ITEM', payload: { planId: item.planId, item } })
        }
      }
      return item ?? null
    } catch (error) {
      return null
    }
  }

  const createItem = async (dreamId: string, item: DreamityItemSaveReqDto) => {
    try {
      dreamityPlannerRepository.createItem(dreamId, item)
      const items = dreamityPlannerRepository.listItems(dreamId, item.planId)
      dispatch({ type: 'SET_PLAN_ITEMS', payload: { planId: item.planId, items } })
      scheduleDebouncedPushDreamitySnapshot()
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const updateItem = async (_dreamId: string, itemId: string, item: DreamityItemModifyReqDto) => {
    try {
      const updated = dreamityPlannerRepository.updateItem(itemId, item)
      if (updated && updated.planId) {
        dispatch({ type: 'UPDATE_ITEM', payload: { planId: updated.planId, item: updated } })
      }
      scheduleDebouncedPushDreamitySnapshot()
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    }
  }

  const deleteItem = async (_dreamId: string, itemId: string, planId: string) => {
    try {
      dreamityPlannerRepository.deleteItem(itemId)
      dispatch({ type: 'DELETE_ITEM', payload: { planId, itemId } })
      scheduleDebouncedPushDreamitySnapshot()
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    }
  }

  const selectItem = (item: DreamityItemResDto | null) => {
    dispatch({ type: 'SET_SELECTED_ITEM', payload: item })
  }

  // 모든 아이템을 하나의 리스트로 합쳐서 반환 (GoalDetail 등에서 사용)
  const allItems = useMemo(() => {
    return Object.values(state.itemsMap).flat()
  }, [state.itemsMap])

  return (
    <ItemContext.Provider
      value={{
        ...state,
        items: allItems,
        fetchItems,
        fetchItemById,
        createItem,
        updateItem,
        deleteItem,
        selectItem,
      }}
    >
      {children}
    </ItemContext.Provider>
  )
}

export function useItemContext() {
  const context = useContext(ItemContext)
  if (context === undefined) {
    throw new Error('useItem must be used within an ItemProvider')
  }
  return context
}
